import { useState } from "react";
import { useNavigate } from "react-router-dom";
import api from "@/lib/api";
import Header from "@/components/Header";
import { toast } from "sonner";
import { Save, Sparkles } from "lucide-react";
import { divisionsForLevel } from "@/lib/divisions";
import { VerifyStep } from "@/pages/PostJob";

const SPORTS = [
  { value: "Lacrosse", enabled: true },
  { value: "Football", enabled: false },
  { value: "Basketball", enabled: false },
  { value: "Baseball", enabled: false },
  { value: "Soccer", enabled: false },
  { value: "Volleyball", enabled: false },
];
const GENDERS = ["Men", "Women", "Coed"];
const LEVELS = ["MS", "HS", "JUCO", "College"];
const ROLES = ["Head Coach", "Associate Head Coach", "Assistant Coach", "Offensive Coordinator", "Defensive Coordinator", "Director of Operations", "Graduate Assistant", "Volunteer Assistant"];
const REGIONS = ["Northeast", "Southeast", "Upper Midwest", "Lower Midwest", "Northwest", "Southwest"];
const SETTINGS = ["Public", "Private"];
const SELECTIVITY = ["Most Selective", "More Selective", "Less Selective", "Least Selective"];
const ENROLLMENT = ["<3,000", "3,000-10,000", "10,000-20,000", ">20,000"];
const SCHOLARSHIPS = ["Full", "Ivy (Need-based)", "Partial", "None"];
const SALARY_BANDS = ["Commensurate with experience", "0-5,000", "5,000-10,000", "10,000-25,000", "25,000-50,000", "50,000-75,000", "75,000-100,000", "100,000-200,000", "200,000+"];
const JOB_TYPES = [
  { value: "Full-Time Salary", label: "Full-Time Salary", hint: "Salaried role · full-time hours (>= 30/wk typical)" },
  { value: "Part-Time Stipend", label: "Part-Time Stipend", hint: "Seasonal or hourly stipend · under 30 hrs/wk typical" },
  { value: "Volunteer", label: "Volunteer / Unpaid", hint: "Unpaid position (grad student, alumni volunteer, etc.)" },
];

export default function JobNew() {
  const nav = useNavigate();
  const [form, setForm] = useState({
    title: "", sport: "Lacrosse", gender: "Men", level: "College", role: "Head Coach",
    division: "D3", conference: "", setting: "Private", selectivity: "More Selective",
    enrollment: "3,000-10,000", scholarship_type: "Partial", nil_available: false, region: "Northeast",
    location: "", job_type: "Full-Time Salary", stipend: "", months: "10", hours: "40-50",
    salary_band: "Commensurate with experience",
    application_deadline: "", start_date: "", position_timing: "immediate",
    description: "",
    needs: { program_status: "rebuilding", philosophy_priority: "", cultural_fit: "", academic_focus: "", resources: "", challenges: "", extra_notes: "" },
    facilities: {}, camps_clinics: {},
    anonymous_listing: false,
    anonymized_title: "",
    is_faculty_position: "none",  // "required" | "preferred" | "none"
    faculty_subjects_needed: [],
  });
  const [conversational, setConversational] = useState("");
  const [interpreting, setInterpreting] = useState(false);
  const [busy, setBusy] = useState(false);
  const [pending, setPending] = useState(null); // unverified program email → code step before the job goes public
  const [verifyEmail, setVerifyEmail] = useState({ contact_email: "" });

  const u = (k, v) => setForm({ ...form, [k]: v });
  const uNeeds = (k, v) => setForm({ ...form, needs: { ...form.needs, [k]: v } });
  const uFac = (k, v) => setForm({ ...form, facilities: { ...form.facilities, [k]: v } });

  const interpret = async () => {
    if (!conversational.trim()) return toast.warning("Describe the role in plain English first.");
    setInterpreting(true);
    try {
      const { data } = await api.post("/program/needs/interpret", { text: conversational });
      setForm({ ...form, needs: { ...form.needs, ...data } });
      toast.success("ProgramNeedsAgent translated your description.");
    } catch (e) {
      toast.error(e.response?.data?.detail || "Interpretation failed");
    } finally { setInterpreting(false); }
  };

  const save = async () => {
    if (!form.title || !form.description) return toast.warning("Title and description are required");
    setBusy(true);
    try {
      const { data } = await api.post("/jobs", form);
      if (data.verify_required) {
        setPending({ job_id: data.id, masked_email: data.contact_email ? data.contact_email.replace(/^(.)(.*)(.@.*)$/, (m, a, b, c) => a + "*".repeat(Math.max(b.length, 1)) + c) : "your email" });
        setVerifyEmail({ contact_email: data.contact_email || "" });
        toast.info("Saved privately — enter the 6-digit code we emailed you to publish it");
        window.scrollTo(0, 0);
        return;
      }
      toast.success("Job posted. Generating matches…");
      nav(`/jobs/${data.id}`);
    } catch (e) {
      toast.error(e.response?.data?.detail || "Post failed");
    } finally { setBusy(false); }
  };

  return (
    <div className="min-h-screen bg-appbg text-white">
      <Header />
      <div className="px-4 md:px-8 lg:px-12 py-8 max-w-5xl" data-testid="job-new-page">
        <div className="text-xs tracking-widest text-blaze">PROGRAMNEEDSAGENT</div>
        <h1 className="heading text-4xl md:text-5xl mt-2">POST A JOB</h1>
        {pending && (
          <div className="mt-6 border border-volt p-6 bg-[#0D0D14]" data-testid="ad-verify-panel">
            <VerifyStep pending={pending} form={verifyEmail} setForm={setVerifyEmail} onVerified={(d) => { toast.success("Published"); nav(`/jobs/${d.job_id}`); }} />
          </div>
        )}
        <p className="text-neutral-400 mt-2 max-w-xl">Describe the role in plain English. Our agent translates it into structured TKG attributes.</p>

        {/* Conversational input */}
        <div className="mt-8 border border-volt p-6 bg-[#0D0D14]" data-testid="conversational-panel">
          <div className="text-xs tracking-widest text-volt mb-2">CONVERSATIONAL BRIEF (OPTIONAL)</div>
          <textarea
            className="input min-h-[100px]"
            data-testid="conversational-text"
            placeholder="e.g. We're a rebuilding D3 men's lacrosse program. Need a defensive-minded head coach who prioritizes academics, can connect with student-athletes in underserved areas, and is comfortable with shared facilities and a modest budget."
            value={conversational}
            onChange={(e) => setConversational(e.target.value)}
          />
          <button
            onClick={interpret}
            disabled={interpreting}
            data-testid="interpret-btn"
            className="mt-3 bg-volt text-white heading text-base px-5 py-2.5 hover:glow-volt transition-all disabled:opacity-50 flex items-center gap-2"
          >
            <Sparkles size={16} /> {interpreting ? "INTERPRETING…" : "INTERPRET WITH AI"}
          </button>
        </div>

        <Section title="THE ROLE">
          <Field label="Job Title"><input className="input" data-testid="job-title" value={form.title} onChange={(e) => u("title", e.target.value)} placeholder="Head Men's Lacrosse Coach" /></Field>
          <Grid cols={4}>
            <Field label="Sport"><select className="input" data-testid="job-sport" value={form.sport} onChange={(e) => u("sport", e.target.value)}>{SPORTS.map((s) => <option key={s.value} value={s.value} disabled={!s.enabled}>{s.value}{s.enabled ? "" : " — coming soon"}</option>)}</select></Field>
            <Field label="Gender"><select className="input" data-testid="job-gender" value={form.gender} onChange={(e) => u("gender", e.target.value)}>{GENDERS.map((s) => <option key={s}>{s}</option>)}</select></Field>
            <Field label="Level"><select className="input" data-testid="job-level" value={form.level} onChange={(e) => u("level", e.target.value)}>{LEVELS.map((s) => <option key={s}>{s}</option>)}</select></Field>
            <Field label="Role"><select className="input" data-testid="job-role" value={form.role} onChange={(e) => u("role", e.target.value)}>{ROLES.map((s) => <option key={s}>{s}</option>)}</select></Field>
            <Field label="Division">
              <select className="input" data-testid="job-division" value={form.division} onChange={(e) => u("division", e.target.value)}>
                {divisionsForLevel(form.level).map((s) => <option key={s}>{s}</option>)}
              </select>
            </Field>
            <Field label="Conference"><input className="input" data-testid="job-conference" value={form.conference} onChange={(e) => u("conference", e.target.value)} /></Field>
            <Field label="Setting"><select className="input" data-testid="job-setting" value={form.setting} onChange={(e) => u("setting", e.target.value)}>{SETTINGS.map((s) => <option key={s}>{s}</option>)}</select></Field>
            <Field label="School Academic Selectivity (acceptance rate + academic standards)"><select className="input" data-testid="job-selectivity" value={form.selectivity} onChange={(e) => u("selectivity", e.target.value)}>{SELECTIVITY.map((s) => <option key={s}>{s}</option>)}</select></Field>
            <Field label="Enrollment"><select className="input" data-testid="job-enrollment" value={form.enrollment} onChange={(e) => u("enrollment", e.target.value)}>{ENROLLMENT.map((s) => <option key={s}>{s}</option>)}</select></Field>
            <Field label="Scholarship"><select className="input" data-testid="job-scholarship" value={form.scholarship_type} onChange={(e) => u("scholarship_type", e.target.value)}>{SCHOLARSHIPS.map((s) => <option key={s}>{s}</option>)}</select></Field>
            <Field label="NIL Available">
              <label className="flex items-center gap-2 cursor-pointer mt-2 text-sm">
                <input
                  type="checkbox"
                  data-testid="job-nil-available"
                  checked={!!form.nil_available}
                  onChange={(e) => u("nil_available", e.target.checked)}
                  className="w-4 h-4 accent-volt cursor-pointer"
                />
                <span className="text-neutral-300">Program offers NIL money / brand deals</span>
              </label>
            </Field>
            <Field label="Region"><select className="input" data-testid="job-region" value={form.region} onChange={(e) => u("region", e.target.value)}>{REGIONS.map((s) => <option key={s}>{s}</option>)}</select></Field>
            <Field label="Salary Band"><select className="input" data-testid="job-salary" value={form.salary_band} onChange={(e) => u("salary_band", e.target.value)}>{SALARY_BANDS.map((s) => <option key={s}>{s}</option>)}</select></Field>
            <Field label="Location (City, State)"><input className="input" data-testid="job-location" value={form.location} onChange={(e) => u("location", e.target.value)} /></Field>
            <Field label="Position Timing">
              <select className="input" data-testid="job-timing" value={form.position_timing} onChange={(e) => u("position_timing", e.target.value)}>
                <option value="immediate">Available immediately</option>
                <option value="next_season">Starts at the conclusion of the current season</option>
              </select>
            </Field>
            <Field label="Application deadline (optional)"><input className="input" type="date" data-testid="job-deadline" value={form.application_deadline || ""} onChange={(e) => u("application_deadline", e.target.value)} /></Field>
            <Field label="Start date (optional)"><input className="input" type="date" data-testid="job-start-date" value={form.start_date || ""} onChange={(e) => u("start_date", e.target.value)} /></Field>
            <Field label="Compensation Type *">
              <select className="input" data-testid="job-type" value={form.job_type} onChange={(e) => u("job_type", e.target.value)}>
                {JOB_TYPES.map((t) => <option key={t.value} value={t.value}>{t.label}</option>)}
              </select>
              <div className="text-[10px] text-neutral-500 mt-1 tracking-widest" data-testid="job-type-hint">
                {JOB_TYPES.find((t) => t.value === form.job_type)?.hint}
              </div>
            </Field>
            {String(form.job_type).toLowerCase().includes("part") && (
              <Field label="Stipend (shown prominently to coaches)">
                <input className="input" data-testid="job-stipend" value={form.stipend || ""} onChange={(e) => u("stipend", e.target.value)} placeholder="e.g. $4,500 / season" />
                <div className="text-[10px] text-neutral-500 mt-1 tracking-widest">Part-time roles convert best when the stipend is stated up front.</div>
              </Field>
            )}
            <Field label="Months"><input className="input" data-testid="job-months" value={form.months} onChange={(e) => u("months", e.target.value)} /></Field>
            <Field label="Hours/week"><input className="input" data-testid="job-hours" value={form.hours} onChange={(e) => u("hours", e.target.value)} /></Field>
          </Grid>
        </Section>

        <Section title="STRUCTURED NEEDS (AUTO-FILLED BY AGENT)">
          <Grid cols={2}>
            <Field label="Program Status">
              <select className="input" data-testid="needs-status" value={form.needs.program_status || ""} onChange={(e) => uNeeds("program_status", e.target.value)}>
                <option value="rebuilding">Rebuilding</option>
                <option value="established_contender">Established Contender</option>
                <option value="transitional">Transitional</option>
              </select>
            </Field>
            <Field label="Philosophy Priority"><input className="input" data-testid="needs-philosophy" value={form.needs.philosophy_priority || ""} onChange={(e) => uNeeds("philosophy_priority", e.target.value)} /></Field>
            <Field label="Cultural Fit"><input className="input" data-testid="needs-culture" value={form.needs.cultural_fit || ""} onChange={(e) => uNeeds("cultural_fit", e.target.value)} /></Field>
            <Field label="Academic Focus"><input className="input" data-testid="needs-academic" value={form.needs.academic_focus || ""} onChange={(e) => uNeeds("academic_focus", e.target.value)} /></Field>
            <Field label="Resources"><input className="input" data-testid="needs-resources" value={form.needs.resources || ""} onChange={(e) => uNeeds("resources", e.target.value)} /></Field>
            <Field label="Challenges"><input className="input" data-testid="needs-challenges" value={form.needs.challenges || ""} onChange={(e) => uNeeds("challenges", e.target.value)} /></Field>
          </Grid>
        </Section>

        <Section title="FACILITIES AVAILABLE">
          <p className="text-xs text-neutral-500 -mt-2">Tell candidate coaches exactly what they&apos;ll be working with. Mark each surface as <b className="text-volt">Priority</b> (your team has first dibs on scheduling) or <b className="text-volt">Share</b> (rotated with other programs).</p>
          <Grid cols={2}>
            <FacField label="Practice Field · Turf" value={form.facilities.practice_field_turf} onChange={(v) => uFac("practice_field_turf", v)} options={["priority", "share", "na"]} testid="fac-prac-turf" />
            <FacField label="Practice Field · Grass" value={form.facilities.practice_field_grass} onChange={(v) => uFac("practice_field_grass", v)} options={["priority", "share", "na"]} testid="fac-prac-grass" />
            <FacField label="Game Field · Turf" value={form.facilities.game_field_turf} onChange={(v) => uFac("game_field_turf", v)} options={["priority", "share", "na"]} testid="fac-game-turf" />
            <FacField label="Game Field · Grass" value={form.facilities.game_field_grass} onChange={(v) => uFac("game_field_grass", v)} options={["priority", "share", "na"]} testid="fac-game-grass" />
            <FacField label="Weight Room" value={form.facilities.weight_room} onChange={(v) => uFac("weight_room", v)} options={["priority", "share", "na"]} testid="fac-weight" />
            <FacField label="Locker Room" value={form.facilities.locker_room} onChange={(v) => uFac("locker_room", v)} options={["exclusive", "share", "na"]} testid="fac-locker" />
          </Grid>
          <Field label="Indoor Facility">
            <div className="flex items-center gap-3">
              <button
                type="button"
                data-testid="toggle-indoor-facility"
                onClick={() => uFac("indoor_facility", !form.facilities.indoor_facility)}
                className={`h-11 px-4 text-xs uppercase tracking-widest border ${form.facilities.indoor_facility ? "bg-volt border-volt text-white" : "border-neutral-700 text-neutral-400"}`}
              >
                {form.facilities.indoor_facility ? "AVAILABLE" : "NOT AVAILABLE"}
              </button>
              {form.facilities.indoor_facility && (
                <input className="input flex-1" data-testid="fac-indoor-notes" placeholder="e.g. shared bubble Nov-Mar, 60x100" value={form.facilities.indoor_facility_notes || ""} onChange={(e) => uFac("indoor_facility_notes", e.target.value)} />
              )}
            </div>
          </Field>
          <Field label="Other Facilities (free text)">
            <textarea className="input min-h-[60px]" data-testid="fac-other" placeholder="e.g. athletic training room, film lab, hydrotherapy/recovery suite, dedicated lacrosse equipment storage" value={form.facilities.other_facilities || ""} onChange={(e) => uFac("other_facilities", e.target.value)} />
          </Field>
        </Section>

        <Section title="DESCRIPTION">
          <Field label="Public Job Description"><textarea className="input min-h-[120px]" data-testid="job-description" value={form.description} onChange={(e) => u("description", e.target.value)} placeholder="Describe the role, expectations, and program context." /></Field>
        </Section>

        {form.level === "HS" && (
          <Section title="HS COACH + FACULTY HYBRID">
            <p className="text-xs text-neutral-500 -mt-2">Most HS coaching jobs come paired with classroom teaching. Tell us your shape — coaches who can teach the right subject(s) will surface as stronger matches. &ldquo;Preferred&rdquo; means flexible (you&apos;ll also consider coach-only candidates).</p>
            {form.job_type && form.job_type.toLowerCase().includes("full-time") && (
              <div
                className="border-l-4 border-volt bg-volt/10 px-4 py-3 text-sm text-neutral-200"
                data-testid="ft-hs-teaching-callout"
              >
                <b className="text-volt tracking-wide">FULL-TIME HS COACHING ROLE.</b>{" "}
                In practice, a full-time HS coaching position almost always
                includes a classroom teaching assignment. Pick <b className="text-white">Required</b> or{" "}
                <b className="text-white">Preferred</b> below and list every subject a candidate&apos;s
                degree must cover — we&apos;ll only surface coaches whose credentials match.
              </div>
            )}
            <Field label="Faculty / teaching component">
              <div className="flex flex-wrap gap-2" data-testid="is-faculty-toggle">
                {[
                  { v: "required",  label: "Required — teacher + coach" },
                  { v: "preferred", label: "Preferred — flexible" },
                  { v: "none",      label: "None — coaching only" },
                ].map((opt) => {
                  const on = (form.is_faculty_position || "none") === opt.v;
                  return (
                    <button
                      type="button"
                      key={opt.v}
                      data-testid={`job-faculty-${opt.v}`}
                      onClick={() => u("is_faculty_position", opt.v)}
                      className={`px-3 min-h-[36px] text-xs tracking-widest border transition-colors ${on ? "bg-volt text-white border-volt" : "border-neutral-700 text-neutral-400 hover:border-volt"}`}
                    >
                      {opt.label.toUpperCase()}
                    </button>
                  );
                })}
              </div>
            </Field>
            {(form.is_faculty_position === "required" || form.is_faculty_position === "preferred") && (
              <Field label="Teaching subject(s) the coach must be degree-qualified to teach (pick all that apply — multiple selections OK)">
                <div className="flex flex-wrap gap-2" data-testid="faculty-subjects-needed">
                  {["Math", "Science", "English", "Social Studies", "Language", "Fine Arts", "Computer Science", "Physical Education", "Counselor", "School Administration", "Other"].map((s) => {
                    const on = (form.faculty_subjects_needed || []).includes(s);
                    return (
                      <button
                        type="button"
                        key={s}
                        data-testid={`job-subject-${s.toLowerCase().replace(/\s+/g, "-")}`}
                        onClick={() => u("faculty_subjects_needed", on ? form.faculty_subjects_needed.filter((x) => x !== s) : [...(form.faculty_subjects_needed || []), s])}
                        className={`px-3 min-h-[36px] text-xs tracking-widest border transition-colors ${on ? "bg-volt text-white border-volt" : "border-neutral-700 text-neutral-300 hover:border-volt"}`}
                      >
                        {s.toUpperCase()}
                      </button>
                    );
                  })}
                </div>
                {(form.faculty_subjects_needed || []).length === 0 && form.is_faculty_position === "required" && (
                  <div className="text-[11px] text-blaze mt-2" data-testid="ft-teaching-warning">
                    ⚠ Required teacher + coach but no subjects chosen — coaches won&apos;t be filtered by degree fit. Pick at least one subject.
                  </div>
                )}
              </Field>
            )}
          </Section>
        )}

        <Section title="ANONYMITY">
          <p className="text-xs text-neutral-500 -mt-2">Hide your program name from public listings. Reveal only to coaches who pass an initial fit and the AD chooses to invite.</p>
          <Field label="Post anonymously (hide program name in listings)">
            <button
              type="button"
              data-testid="toggle-anonymous-listing"
              onClick={() => u("anonymous_listing", !form.anonymous_listing)}
              className={`h-11 px-4 text-xs uppercase tracking-widest border ${form.anonymous_listing ? "bg-volt border-volt text-white" : "border-neutral-700 text-neutral-400"}`}
            >
              {form.anonymous_listing ? "ANONYMOUS POSTING ON" : "PUBLIC POSTING"}
            </button>
          </Field>
          {form.anonymous_listing && (
            <Field label="Public display title (what coaches see instead of your program name)">
              <input
                className="input"
                data-testid="job-anonymized-title"
                value={form.anonymized_title}
                onChange={(e) => u("anonymized_title", e.target.value)}
                placeholder="e.g. Top-15 D1 East Coast Men's Lacrosse program"
              />
            </Field>
          )}
        </Section>

        <button onClick={save} disabled={busy} data-testid="post-job-btn" className="w-full mt-8 bg-blaze text-white heading text-xl py-4 min-h-[56px] hover:glow-blaze transition-all disabled:opacity-50 flex items-center justify-center gap-2">
          <Save size={18} /> {busy ? "POSTING…" : "POST JOB & GENERATE MATCHES"}
        </button>
      </div>
      <style>{`.input { width: 100%; background: #121212; border: 1px solid #27272A; padding: 10px 12px; color: white; outline: none; font-size: 13px; }
      .input:focus { border-color: #FF3B30; }`}</style>
    </div>
  );
}

function Section({ title, children }) {
  return (
    <div className="mt-6 border border-neutral-800 p-6 bg-appcard">
      <div className="text-xs tracking-widest text-volt mb-4">{title}</div>
      <div className="space-y-4">{children}</div>
    </div>
  );
}
function Grid({ cols, children }) { return <div className={`grid grid-cols-1 md:grid-cols-${cols} gap-3`}>{children}</div>; }
function Field({ label, children }) {
  return <label className="block"><div className="text-[10px] tracking-widest text-neutral-500 mb-1.5">{label.toUpperCase()}</div>{children}</label>;
}

function FacField({ label, value, onChange, options, testid }) {
  const LABEL_MAP = { priority: "PRIORITY", share: "SHARE", exclusive: "EXCLUSIVE", na: "N/A" };
  return (
    <Field label={label}>
      <div className="flex gap-px bg-neutral-800" data-testid={testid}>
        {options.map((opt) => (
          <button
            key={opt}
            type="button"
            data-testid={`${testid}-${opt}`}
            onClick={() => onChange(value === opt ? null : opt)}
            className={`flex-1 py-2.5 text-[10px] tracking-widest uppercase transition-colors ${value === opt ? "bg-volt text-white" : "bg-appcard text-neutral-400 hover:text-white"}`}
          >
            {LABEL_MAP[opt] || opt.toUpperCase()}
          </button>
        ))}
      </div>
    </Field>
  );
}
